import React, { useState, useEffect } from 'react';
import { Plus, CreditCard as Edit, Trash2, BarChart, TrendingUp, Users, Globe, DollarSign, Eye } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Metric {
  id: string;
  key: string;
  value: string;
  description: string | null;
  updated_at: string;
}

export default function MetricsManager() {
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMetric, setEditingMetric] = useState<Metric | null>(null);

  const [newMetric, setNewMetric] = useState({
    key: '',
    value: '',
    description: ''
  });

  const predefinedMetrics = [
    { key: 'total_projects', label: 'Total Projects Completed', icon: BarChart },
    { key: 'active_clients', label: 'Active Clients', icon: Users },
    { key: 'website_visitors', label: 'Monthly Website Visitors', icon: Globe },
    { key: 'client_satisfaction', label: 'Client Satisfaction Rate', icon: TrendingUp },
    { key: 'revenue_growth', label: 'Revenue Growth (%)', icon: DollarSign },
    { key: 'team_members', label: 'Team Members', icon: Users },
    { key: 'years_experience', label: 'Years of Experience', icon: BarChart },
    { key: 'support_response_time', label: 'Average Support Response Time', icon: TrendingUp }
  ];

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const { data, error } = await supabase
        .from('website_metrics')
        .select('*')
        .order('key', { ascending: true });

      if (error) throw error;
      setMetrics(data || []);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMetric = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('website_metrics')
        .insert([newMetric]);

      if (error) throw error;

      setNewMetric({
        key: '',
        value: '',
        description: ''
      });
      setShowCreateModal(false);
      fetchMetrics();
    } catch (error) {
      console.error('Error creating metric:', error);
    }
  };

  const handleUpdateMetric = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMetric) return;

    try {
      const { error } = await supabase
        .from('website_metrics')
        .update({
          value: editingMetric.value,
          description: editingMetric.description
        })
        .eq('id', editingMetric.id);

      if (error) throw error;

      setEditingMetric(null);
      fetchMetrics();
    } catch (error) {
      console.error('Error updating metric:', error);
    }
  };

  const handleDeleteMetric = async (id: string) => {
    if (!confirm('Are you sure you want to delete this metric?')) return;

    try {
      const { error } = await supabase
        .from('website_metrics')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchMetrics();
    } catch (error) {
      console.error('Error deleting metric:', error);
    }
  };

  const getMetricIcon = (key: string) => {
    const predefined = predefinedMetrics.find(m => m.key === key);
    return predefined ? predefined.icon : BarChart;
  };

  const getMetricLabel = (key: string) => {
    const predefined = predefinedMetrics.find(m => m.key === key);
    return predefined ? predefined.label : key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Website Metrics</h1>
          <p className="text-gray-600">Track and manage key performance indicators</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Metric
        </button>
      </div>

      {/* Metrics Grid */}
      {metrics.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <BarChart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No metrics found</h3>
          <p className="text-gray-600">Get started by adding your first website metric.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((metric) => {
            const IconComponent = getMetricIcon(metric.key);
            return (
              <div key={metric.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <IconComponent className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex space-x-1">
                    <button 
                      onClick={() => setEditingMetric(metric)}
                      className="p-1 text-gray-400 hover:text-blue-600"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleDeleteMetric(metric.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                <div className="mb-2">
                  <div className="text-2xl font-bold text-gray-900">{metric.value}</div>
                  <div className="text-sm font-medium text-gray-700">{getMetricLabel(metric.key)}</div>
                </div>
                
                {metric.description && (
                  <p className="text-xs text-gray-500">{metric.description}</p>
                )}
                
                <div className="mt-4 text-xs text-gray-400">
                  Updated {new Date(metric.updated_at).toLocaleDateString()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Metric Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Add New Metric</h2>
              <form onSubmit={handleCreateMetric} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Metric Key
                  </label>
                  <select
                    required
                    value={newMetric.key}
                    onChange={(e) => setNewMetric({ ...newMetric, key: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a metric</option>
                    {predefinedMetrics.map(metric => (
                      <option key={metric.key} value={metric.key}>{metric.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Value
                  </label>
                  <input
                    type="text"
                    required
                    value={newMetric.value}
                    onChange={(e) => setNewMetric({ ...newMetric, value: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 500+, 98%, $1M"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    rows={2}
                    value={newMetric.description}
                    onChange={(e) => setNewMetric({ ...newMetric, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Additional context about this metric"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Add Metric
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Metric Modal */}
      {editingMetric && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Edit Metric</h2>
              <form onSubmit={handleUpdateMetric} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Metric
                  </label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-700">
                    {getMetricLabel(editingMetric.key)}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Value
                  </label>
                  <input
                    type="text"
                    required
                    value={editingMetric.value}
                    onChange={(e) => setEditingMetric({ ...editingMetric, value: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    rows={2}
                    value={editingMetric.description || ''}
                    onChange={(e) => setEditingMetric({ ...editingMetric, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setEditingMetric(null)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Update Metric
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}